import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Save, Trash2, MonitorPlay, Download, FileText } from "lucide-react";

/**
 * Counts characters EXCLUDING whitespace, per spec :
 * « les espaces ne doivent pas être comptés dans les 100 caractères ».
 */
function countNoSpace(s) {
  return [...(s || "")].filter((c) => !/\s/.test(c)).length;
}

const GLOBAL_MESSAGE_MAX = 100;

/**
 * Establishment-wide message displayed at the top of the public screen,
 * independent of any queue. Visible whether the merchant has 1 or 10 queues.
 */
export default function GlobalMessagePanel({ commerce, queues, onChange }) {
  const [msg, setMsg] = useState(commerce?.globalMessage || "");
  const [selected, setSelected] = useState(() =>
    (queues || []).filter((q) => q.displayOnPublic !== false).map((q) => q.id),
  );
  const [saving, setSaving] = useState(false);
  const [savingSel, setSavingSel] = useState(false);

  useEffect(() => {
    setMsg(commerce?.globalMessage || "");
  }, [commerce?.globalMessage]);

  useEffect(() => {
    setSelected((queues || []).filter((q) => q.displayOnPublic !== false).map((q) => q.id));
  }, [queues?.length, queues?.map((q) => `${q.id}:${q.displayOnPublic !== false}`).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const noSpaceCount = countNoSpace(msg);
  const overLimit = noSpaceCount > GLOBAL_MESSAGE_MAX;

  const handleChange = (e) => {
    const next = e.target.value.toUpperCase();
    // Soft-cap: forbid typing past the limit (counter validates anyway).
    if (countNoSpace(next) > GLOBAL_MESSAGE_MAX + 50) return;
    setMsg(next);
  };

  const save = async () => {
    if (overLimit) {
      toast.error("Message trop long (100 caractères hors espaces)");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/merchant/me/global-message", { globalMessage: msg });
      toast.success("Message global mis à jour");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch("/merchant/me/global-message", { globalMessage: "" });
      setMsg("");
      toast.success("Message effacé");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const toggleQueue = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const saveSelection = async () => {
    if ((queues?.length || 0) >= 2 && selected.length === 0) {
      toast.error("Veuillez sélectionner au moins une file à afficher sur l'écran public.");
      return;
    }
    setSavingSel(true);
    try {
      await api.patch("/merchant/me/display-selection", { queueIds: selected });
      toast.success("Sélection enregistrée");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSavingSel(false);
  };

  const hasMultipleQueues = (queues?.length || 0) >= 2;
  const hasAnyQueue = (queues?.length || 0) >= 1;

  const downloadGlobalQrPng = async () => {
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const url = `${apiBase}/commerces/${commerce.id}/global-qr.png`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `BypassGo-QR-Global-${commerce.id.slice(0, 8)}.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Téléchargement impossible"); }
  };

  const downloadGlobalQrPdf = async () => {
    try {
      const token = localStorage.getItem("bypassgo_token") || localStorage.getItem("bypassgo_commerce_token");
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const res = await fetch(`${apiBase}/merchant/global-qr.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("dl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BypassGo-QR-Global-${commerce.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Génération PDF impossible"); }
  };

  return (
    <section id="bpg-global-message-section" className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4" data-testid="global-settings-section">
      {/* GLOBAL QR DOWNLOAD — visible dès qu'il existe au moins une file */}
      {hasAnyQueue && (
        <div className="lg:col-span-2 rounded-2xl border border-border bg-white shadow-sm overflow-hidden" data-testid="global-qr-panel">
          <header className="px-4 sm:px-5 py-3 border-b border-border flex items-center gap-2">
            <Download size={16} className="text-brand" />
            <h3 className="text-sm font-black uppercase tracking-[0.08em] text-ink">
              QR code global de l'établissement
            </h3>
          </header>
          <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-4">
            <img
              src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/commerces/${commerce.id}/global-qr.png`}
              alt="QR code global"
              className="w-32 h-32 border border-border bg-white rounded-md"
              data-testid="global-qr-preview"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-subtle leading-relaxed">
                <span className="font-bold text-ink">QR code global :</span>{" "}
                Un seul QR code pour toutes vos files d'attente. Une fois scanné, le visiteur sélectionne la file souhaitée et prend son ticket.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={downloadGlobalQrPng}
                  className="inline-flex items-center gap-1.5 h-9 px-3 border-2 border-ink text-ink font-bold text-xs hover:bg-surface rounded-md uppercase tracking-wider"
                  data-testid="global-qr-download-png-btn"
                >
                  <Download size={12} /> PNG
                </button>
                <button
                  onClick={downloadGlobalQrPdf}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-ink text-white font-bold text-xs hover:bg-ink/90 rounded-md uppercase tracking-wider"
                  data-testid="global-qr-download-pdf-btn"
                >
                  <FileText size={12} /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
